<template>
  <!-- Dialog mode - with v-dialog wrapper -->
  <v-dialog
    v-if="!inline"
    v-model="isVisible"
    :max-width="maxWidth"
    :attach="attach"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
    persistent
  >
    <v-card
      class="checkpoint-suggestion-modal"
      :class="{ 'edit-mode-background': isEditMode }"
    >
      <CheckpointSuggestionContent
        :isEditMode="isEditMode"
        :isAllDone="isAllDone"
        :isCreating="isCreating"
        :isFormValid="isFormValid"
        :currentIndex="currentIndex"
        :totalCount="totalCount"
        :responsibilityName="responsibilityName"
        :description="description"
        :currentCheckpoint="currentCheckpoint"
        :editedInstructions="editedInstructions"
        :selectedLevelObject="selectedLevelObject"
        :levelOptions="levelOptions"
        :editedCriteria="editedCriteria"
        :editedCriteriaPrefix="editedCriteriaPrefix"
        @close="handleClose"
        @suggest-more="handleSuggestMore"
        @edit="handleEdit"
        @cancel-edit="handleCancelEdit"
        @save-changes="handleSaveChanges"
        @add-checkpoint="handleAddCheckpoint"
        @reject="handleReject"
        @previous="handlePrevious"
        @next="handleNext"
        @level-change="handleLevelChange"
        @update:editedInstructions="editedInstructions = $event"
        @update:editedCriteria="editedCriteria = $event"
        @update:editedCriteriaPrefix="editedCriteriaPrefix = $event"
      />
    </v-card>
  </v-dialog>

  <!-- Inline mode - render without v-dialog wrapper -->
  <v-card 
    v-else 
    class="checkpoint-suggestion-modal" 
    :class="{ 'edit-mode-background': isEditMode }"
  >
    <CheckpointSuggestionContent
      :isEditMode="isEditMode"
      :isAllDone="isAllDone"
      :isCreating="isCreating"
      :isFormValid="isFormValid"
      :currentIndex="currentIndex"
      :totalCount="totalCount"
      :responsibilityName="responsibilityName"
      :description="description"
      :currentCheckpoint="currentCheckpoint"
      :editedInstructions="editedInstructions"
      :selectedLevelObject="selectedLevelObject"
      :levelOptions="levelOptions"
      :editedCriteria="editedCriteria"
      :editedCriteriaPrefix="editedCriteriaPrefix"
      @close="handleClose"
      @suggest-more="handleSuggestMore"
      @edit="handleEdit"
      @cancel-edit="handleCancelEdit"
      @save-changes="handleSaveChanges"
      @add-checkpoint="handleAddCheckpoint"
      @reject="handleReject"
      @previous="handlePrevious"
      @next="handleNext"
      @level-change="handleLevelChange"
      @update:editedInstructions="editedInstructions = $event"
      @update:editedCriteria="editedCriteria = $event"
      @update:editedCriteriaPrefix="editedCriteriaPrefix = $event"
    />
  </v-card>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import CheckpointSuggestionContent from "./CheckpointSuggestionContent.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: [String, Number],
    default: 626,
  },
  attach: {
    type: [String, Boolean],
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  responsibilityName: {
    type: String,
    required: true,
  },
  checkpoints: {
    type: Array,
    default: () => [],
    validator: (value) => {
      return value.every(
        (item) =>
          item &&
          typeof item === "object" &&
          "criteria" in item &&
          "instructions" in item &&
          "level" in item
      );
    },
  },
  description: {
    type: String,
    default:
      "IQ scanned public sources to suggest these Checkpoints. Before suggesting, it looked into current checkpoints to exclude same or similar from suggestions.",
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "close", "suggest-more", "add-checkpoint", "reject-checkpoint", "is-all-done"]);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// Internal state for checkpoints array and current index
const internalCheckpoints = ref([]);
const currentIndex = ref(1);

// Initialize internal checkpoints from props
watch(
  () => props.checkpoints,
  (newCheckpoints) => {
    internalCheckpoints.value = [...newCheckpoints];
    // Reset index to 1 when checkpoints change
    if (internalCheckpoints.value.length > 0) {
      currentIndex.value = 1;
    }
  },
  { immediate: true, deep: true }
);

// State management: 'navigate' or 'edit'
const currentState = ref("navigate");
const isEditMode = computed(() => currentState.value === "edit");

// Local pending state to prevent double-clicks (set immediately on click, before async operations)
const isPendingSubmission = ref(false);
const pendingTimeoutId = ref(null);

// Reset pending state with optional timeout failsafe
const setPendingWithTimeout = () => {
  isPendingSubmission.value = true;
  // Clear any existing timeout
  if (pendingTimeoutId.value) {
    clearTimeout(pendingTimeoutId.value);
  }
  // Failsafe: reset pending after 5 seconds if isCreating never became true
  pendingTimeoutId.value = setTimeout(() => {
    if (isPendingSubmission.value && !props.isCreating) {
      isPendingSubmission.value = false;
    }
  }, 5000);
};

// Edit mode fields
const editedInstructions = ref("");
const editedLevel = ref(null);
const editedCriteria = ref("");
const editedCriteriaPrefix = ref("Can");

// Level options for the picker
const levelOptions = [
  { id: 1, name: "Apprentice" },
  { id: 2, name: "Professional" },
  { id: 3, name: "Coach" },
  { id: 4, name: "Master" },
];

// Get level object from level string
const getLevelObject = (levelString) => {
  return levelOptions.find((l) => l.name === levelString) || levelOptions[0];
};

// Selected level object for the picker
const selectedLevelObject = computed(() => {
  return editedLevel.value || getLevelObject(currentCheckpoint.value.level);
});

// Form validation - check if fields have content
const isFormValid = computed(() => {
  if (!editedCriteria.value) return false;
  return editedCriteria.value.trim().length > 0;
});

// Use internal state
const totalCount = computed(() => internalCheckpoints.value.length);

const currentCheckpoint = computed(() => {
  const listLength = internalCheckpoints.value.length;
  if (listLength === 0) {
    return { criteria: "", instructions: "", level: "Apprentice", prefix: "" };
  }

  const safeIndex = Math.min(Math.max(1, currentIndex.value), listLength);
  const original = internalCheckpoints.value[safeIndex - 1] || {
    criteria: "",
    instructions: "",
    level: "Apprentice",
    prefix: "",
  };

  // If in edit mode, return the edited values
  if (isEditMode.value && editedInstructions.value) {
    return {
      ...original,
      instructions: editedInstructions.value,
    };
  }

  return original;
});

// Check if all checkpoints are done (list is empty)
const isAllDone = computed(() => {
  return totalCount.value === 0;
});

// Watch isAllDone and emit event when it changes
watch(isAllDone, (newValue) => {
  emit("is-all-done", newValue);
}, { immediate: true });

// Expose isAllDone and internal state to parent component
defineExpose({
  isAllDone,
  internalCheckpoints,
  currentIndex,
});

// Watch for changes in current index to reset edit mode
watch(currentIndex, () => {
  if (isEditMode.value) {
    currentState.value = "navigate";
    editedInstructions.value = "";
    editedLevel.value = null;
    editedCriteria.value = "";
    editedCriteriaPrefix.value = "Can";
  }
});

// Watch for modal visibility to reset edit mode when closed
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue && isEditMode.value) {
      currentState.value = "navigate";
      editedInstructions.value = "";
      editedLevel.value = null;
      editedCriteria.value = "";
      editedCriteriaPrefix.value = "Can";
    }
  }
);

// Watch for isCreating prop to handle navigation after creation completes
const wasCreating = ref(false);
const indexBeingCreated = ref(null);
watch(
  () => props.isCreating,
  async (newValue, oldValue) => {
    // When creation starts (was false, now true)
    if (oldValue === false && newValue === true) {
      wasCreating.value = true;
      indexBeingCreated.value = currentIndex.value; // Store the index being created
    }
    
    // When creation completes (was true, now false)
    if (oldValue === true && newValue === false) {
      // Use nextTick to ensure props are updated
      await nextTick();
      
      wasCreating.value = false;
      // Reset the pending flag - creation is complete
      isPendingSubmission.value = false;
      // Clear the failsafe timeout
      if (pendingTimeoutId.value) {
        clearTimeout(pendingTimeoutId.value);
        pendingTimeoutId.value = null;
      }
      
      const createdIndex = indexBeingCreated.value;
      indexBeingCreated.value = null;
      
      // If we were in edit mode during creation, go back to navigate mode
      if (isEditMode.value) {
        currentState.value = "navigate";
        editedInstructions.value = "";
        editedLevel.value = null;
        editedCriteria.value = "";
        editedCriteriaPrefix.value = "Can";
      }
      
      // Now handle the removal and navigation to next item
      const currentTotal = internalCheckpoints.value.length;
      
      // Remove the item from the list
      if (currentTotal > 0 && createdIndex) {
        const indexToRemove = createdIndex - 1;
        internalCheckpoints.value.splice(indexToRemove, 1);
        
        // Navigate after removal
        if (internalCheckpoints.value.length === 0) {
          // No more checkpoints - show completion state
          return;
        } else if (createdIndex === currentTotal) {
          // Was at the last item, move to previous (now the new last item)
          currentIndex.value = internalCheckpoints.value.length;
        }
        // Otherwise stay at the same index (next item slides into place)
      }
    }
  }
);

// Helper function to remove current checkpoint and adjust index
const removeCurrentCheckpoint = () => {
  const indexToRemove = currentIndex.value - 1;
  internalCheckpoints.value.splice(indexToRemove, 1);

  // Adjust current index after removal
  if (internalCheckpoints.value.length === 0) {
    // No more checkpoints - isAllDone will be true
    currentIndex.value = 1;
  } else if (currentIndex.value > internalCheckpoints.value.length) {
    // If we were at the last item, go to the new last item
    currentIndex.value = internalCheckpoints.value.length;
  }
  // Otherwise keep the same index (next item slides into current position)
};

const handleClose = () => {
  emit("close");
  emit("update:modelValue", false);
};

const handleSuggestMore = () => {
  emit("suggest-more");
};

// Parse criteria to extract prefix and text
const parseCriteria = (criteria) => {
  const prefixes = ["Can", "Does", "Demonstrated", "Proved"];
  for (const prefix of prefixes) {
    if (criteria && criteria.startsWith(prefix + " ")) {
      return {
        prefix,
        text: criteria.substring(prefix.length + 1)
      };
    }
  }
  return { prefix: "Can", text: criteria || "" };
};

const handleEdit = () => {
  // Switch to edit mode and populate edit fields
  currentState.value = "edit";
  editedInstructions.value = currentCheckpoint.value.instructions || "";
  editedLevel.value = getLevelObject(currentCheckpoint.value.level);
  
  // Use prefix from checkpoint if available, otherwise parse criteria
  if (currentCheckpoint.value.prefix) {
    editedCriteriaPrefix.value = currentCheckpoint.value.prefix;
    editedCriteria.value = currentCheckpoint.value.criteria || "";
  } else {
    // Fallback: parse criteria to extract prefix
    const parsed = parseCriteria(currentCheckpoint.value.criteria);
    editedCriteria.value = parsed.text;
    editedCriteriaPrefix.value = parsed.prefix;
  }
};

const handleLevelChange = (level) => {
  editedLevel.value = level;
};

const handleCancelEdit = () => {
  // Switch back to navigate mode without saving
  currentState.value = "navigate";
  editedInstructions.value = "";
  editedLevel.value = null;
  editedCriteria.value = "";
  editedCriteriaPrefix.value = "Can";
};

const handleSaveChanges = () => {
  // Only proceed if form is valid
  if (!isFormValid.value) return;

  // Prevent duplicate submissions - check if already creating or pending
  if (props.isCreating || isPendingSubmission.value) return;

  // Set local pending flag immediately to prevent double-clicks (with failsafe timeout)
  setPendingWithTimeout();

  // Get the level name from edited level or current checkpoint
  const levelName = editedLevel.value
    ? editedLevel.value.name
    : currentCheckpoint.value.level;

  // DON'T switch back to navigate mode yet - stay in edit mode
  // The watcher for isCreating will handle the transition after success

  // Emit add-checkpoint event with edited data in proper payload format
  emit("add-checkpoint", {
    checkpoint: {
      level: levelName,
      criteria: editedCriteria.value,
      prefix: editedCriteriaPrefix.value,
      instructions: editedInstructions.value || "<p></p>",
    },
    index: currentIndex.value
  });

  // The watcher for isCreating will handle:
  // 1. Switching back to navigate mode
  // 2. Clearing edit fields
  // 3. Removing the item from the list
  // 4. Navigating to the next item
};

const handleAddCheckpoint = () => {
  // Prevent duplicate submissions - check if already creating or pending
  if (props.isCreating || isPendingSubmission.value) return;

  // Set local pending flag immediately to prevent double-clicks (with failsafe timeout)
  setPendingWithTimeout();

  const checkpoint = currentCheckpoint.value;
  
  // Use prefix from checkpoint if available, otherwise parse criteria
  let prefix = checkpoint.prefix || "";
  let criteriaText = checkpoint.criteria || "";
  
  if (!prefix) {
    // Fallback: parse the criteria to extract prefix
    const parsed = parseCriteria(checkpoint.criteria);
    prefix = parsed.prefix;
    criteriaText = parsed.text;
  }
  
  // Emit the checkpoint data with proper payload format
  // DON'T remove the item immediately - wait for isCreating to become false
  emit("add-checkpoint", {
    checkpoint: {
      level: checkpoint.level,
      criteria: criteriaText,
      prefix: prefix,
      instructions: checkpoint.instructions || "<p></p>",
    },
    index: currentIndex.value
  });
  
  // The watcher for isCreating will handle:
  // 1. Removing the item from the list
  // 2. Navigating to the next item
};

const handleReject = () => {
  // Emit the rejection event
  const checkpoint = currentCheckpoint.value;
  emit("reject-checkpoint", {
    checkpoint: checkpoint,
    index: currentIndex.value
  });
  
  // Remove the current checkpoint from suggestions (it's been rejected)
  removeCurrentCheckpoint();
};

const handlePrevious = () => {
  if (currentIndex.value > 1) {
    currentIndex.value = currentIndex.value - 1;
  }
};

const handleNext = () => {
  if (currentIndex.value < totalCount.value) {
    currentIndex.value = currentIndex.value + 1;
  }
};
</script>

<style scoped>
@import "./checkpointSuggestionModal.css";
</style>
